import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
  memo,
} from "react";
import { CarNumber } from "../types/codeType";
import styles from "./CodesList.module.css";
import config from "../config";
import { useUser } from "../contexts/UserContext";
import ExcelIcon from "../components/icons/ExcelIcon";
import TrashIcon from "../components/icons/TrashIcon";
import AddItemIcon from "../components/icons/AddItemIcon";
import DownloadIcon from "../components/icons/DownloadIcon";
import SelectBox from "../components/SelectBox";
import SortIcon from "../components/icons/SortIcon";
import SummaryModal from "../modals/SummaryModal";
import AllCarsSummary from "../components/carsList/AllCarsSummary";
import TsumodCarsSummary from "../components/carsList/TsumodCarsSummary";
import MaagarCarsSummary from "../components/carsList/MaagarCarsSummary";
import RentedCarsSummary from "../components/carsList/RentedCarsSummary";
import InactiveCarsSummary from "../components/carsList/InactiveCarsSummary";
import { USER_TYPES } from "../types/userType";
import Switch from "@mui/material/Switch";
import Menu3Dots from "../components/icons/Menu3Dots";
import Button from "../components/UI/Button";
import Input from "../components/UiElements/Input";
import DSButton from "../components/UiElements/Button";
import { AnimatePresence, motion } from "framer-motion";
// Single universal search input component
const UniversalSearchInput = memo(
  ({
    searchQuery,
    setSearchQuery,
    searchInputRef,
  }: {
    searchQuery: string;
    setSearchQuery: (v: string) => void;
    searchInputRef: React.RefObject<HTMLInputElement | null>;
  }) => {
    return (
      <Input
        ref={searchInputRef}
        type="text"
        placeholder="חפש לפי מספר רכב, טלפון..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className={styles.searchInput}
        rtl
        size="medium"
        color="outline"
        width="250px"
      />
    );
  }
);

const CodesList: React.FC = () => {
  const userType = localStorage.getItem("userType");
  const isRental =
    userType?.includes(USER_TYPES.RENTAL) &&
    !userType?.includes(USER_TYPES.ADMIN) &&
    !userType?.includes(USER_TYPES.SUPER_ADMIN);
  const [cars, setCars] = useState<CarNumber[]>([]);
  const { loading, setLoading, loadingMore, setLoadingMore } = useUser();
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadCars, setReloadCars] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState<CarNumber | null>(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<string[]>([]);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsData, setDetailsData] = useState<any>(null);
  const [selectedCars, setSelectedCars] = useState<string[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCar, setEditCar] = useState<CarNumber | null>(null);
  const [editCategory, setEditCategory] = useState<string>("");
  const [editExpirationDate, setEditExpirationDate] = useState<string>("");
  const [editStartDate, setEditStartDate] = useState<string>("");
  const [editSystemIdentifier, setEditSystemIdentifier] = useState<string>("");
  const [editDealNumber, setEditDealNumber] = useState<string>("");
  const [editFirstName, setEditFirstName] = useState<string>("");
  const [editLastName, setEditLastName] = useState<string>("");
  const [editMobilePhone, setEditMobilePhone] = useState<string>("");
  const [editPersonalId, setEditPersonalId] = useState<string>("");
  const [editManufacturer, setEditManufacturer] = useState<string>("");
  const [editModel, setEditModel] = useState<string>("");
  const [editUnit, setEditUnit] = useState<string>("");
  const [editVip, setEditVip] = useState<boolean>(false);
  const [editVipType, setEditVipType] = useState<"unlimited" | "one-time">(
    "unlimited"
  );
  const [editError, setEditError] = useState<string>("");
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null
  );
  const [sortStatus, setSortStatus] = useState<
    "all" | "tsumod" | "maagar" | "rented" | "blocked" | "inactive" | "vip"
  >("all");
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>("");
  const [updatingBlocked, setUpdatingBlocked] = useState<string | null>(null);
  const [updatingVip, setUpdatingVip] = useState<string | null>(null);
  const [reactivatingCarNumber, setReactivatingCarNumber] = useState<
    string | null
  >(null);
  const [vipTypeState, setVipTypeState] = useState<
    Record<string, "unlimited" | "one-time">
  >({});
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const initialLoadDoneRef = useRef(false);
  const [visibleRowsCount, setVisibleRowsCount] = useState(400);

  // Add car modal states
  const [showAddCarModal, setShowAddCarModal] = useState(false);
  const [addCarNumber, setAddCarNumber] = useState<string>("");
  const [addCategory, setAddCategory] = useState<string>("צמוד");
  const [addExpirationDate, setAddExpirationDate] = useState<string>("");
  const [addStartDate, setAddStartDate] = useState<string>("");
  const [addManufacturer, setAddManufacturer] = useState<string>("");
  const [addModel, setAddModel] = useState<string>("");
  const [addUnit, setAddUnit] = useState<string>("");
  const [addVip, setAddVip] = useState<boolean>(false);
  const [addVipType, setAddVipType] = useState<"unlimited" | "one-time">(
    "unlimited"
  );
  const [addError, setAddError] = useState<string>("");

  // Fetch codes from backend
  const getCars = async (page = 1, reset = false, searchQuery?: string) => {
    if (page === 1 || reset) {
      setLoading(true);
      setCurrentPage(1);
    } else {
      setLoadingMore(true);
    }

    try {
      let carsList: CarNumber[] = [];
      // If searching, get all data and filter on frontend
      // For regular pagination, use backend pagination
      if (searchQuery) {
        // For search, fetch all data using pagination like loadAllCars
        let allCars: CarNumber[] = [];
        let searchPage = 1;
        let hasMoreData = true;

        while (hasMoreData) {
          try {
            let url = isRental
              ? `${config.backendUrl}/car-numbers/rental-list?page=${searchPage}&limit=2000`
              : `${config.backendUrl}/car-numbers/list?page=${searchPage}&limit=2000`;
            const res = await fetch(url, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
              },
            });
            const data = await res.json();
            const carsList = data.carNumbers || [];
            allCars = [...allCars, ...carsList];
            hasMoreData =
              data.hasMore !== undefined
                ? data.hasMore
                : carsList.length === 2000;
            searchPage++;
          } catch (e) {
            hasMoreData = false;
          }
        }

        // Apply universal search filter
        const query = searchQuery.toLowerCase();
        carsList = allCars.filter((c) => {
          // Search across multiple fields
          const carNumber = c.carNumber?.toLowerCase() || "";
          const phone = c.mobilePhone?.toLowerCase() || "";
          const firstName = c.firstName?.toLowerCase() || "";
          const lastName = c.lastName?.toLowerCase() || "";
          const fullName = `${firstName} ${lastName}`.trim();
          const systemId = c.systemIdentifier?.toLowerCase() || "";
          const manufacturer = c.manufacturer?.toLowerCase() || "";
          const personalId = c.personalId?.toLowerCase() || "";

          return (
            carNumber.includes(query) ||
            phone.includes(query) ||
            firstName.includes(query) ||
            lastName.includes(query) ||
            fullName.includes(query) ||
            systemId.includes(query) ||
            manufacturer.includes(query) ||
            personalId.includes(query)
          );
        });

        setCars(carsList);
        setHasMore(false); // No pagination for search
      } else {
        // Regular pagination
        let url = isRental
          ? `${config.backendUrl}/car-numbers/rental-list?page=${page}&limit=100`
          : `${config.backendUrl}/car-numbers/list?page=${page}&limit=100`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        });
        const data = await res.json();
        carsList = data.carNumbers || [];

        if (page === 1 || reset) {
          setCars(carsList);
        } else {
          setCars((prev) => [...prev, ...carsList]);
        }
        setHasMore(
          data.hasMore !== undefined ? data.hasMore : carsList.length === 100
        );
      }

      setCurrentPage(page);
    } catch (error) {
      setCars([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initial load - get all cars and sort by received codes
  useEffect(() => {
    const loadAllInitially = async () => {
      setLoading(true);
      await loadAllCars();
      // Set initial sort to receivedCodes descending (cars with codes first)
      setSortColumn("receivedCodes");
      setSortDirection("desc");
      initialLoadDoneRef.current = true;
      setLoading(false);
    };
    loadAllInitially();
    getManufacturers();
  }, []);

  // Handle reloads when reloadCars changes (but not on initial mount)
  useEffect(() => {
    if (reloadCars > 0) {
      // Only reload if it's been triggered (counter > 0)
      // Clear search when reloading
      setSearchQuery("");
      setSearchActive(false);
      const reloadAllCars = async () => {
        setLoading(true);
        setHasMore(true); // Reset pagination state
        setCurrentPage(1);
        await loadAllCars();
        // Maintain sort by received codes after reload
        setSortColumn("receivedCodes");
        setSortDirection("desc");
        setLoading(false);
      };
      reloadAllCars();
      getManufacturers();
    }
  }, [reloadCars]);

  // Fetch manufacturers from backend
  const getManufacturers = async () => {
    try {
      const res = await fetch(
        `${config.backendUrl}/car-numbers/manufacturers`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const trimmed = list
        .map((m: unknown) => String(m).trim())
        .filter((m) => m && m !== "לא פעילים");
      setManufacturers(Array.from(new Set(trimmed)));
    } catch (error) {
      console.error("Error fetching manufacturers:", error);
      setManufacturers([]);
    }
  };

  useEffect(() => {
    // Don't trigger search on initial mount when searchQuery is empty
    if (searchQuery === "") {
      setSearchActive(false);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      const query = searchQuery.trim();
      getCars(1, true, query);
      setSearchActive(!!query);
    }, 800);
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [searchQuery]);

  const handleToggleActionsMenu = () => {
    setActionsMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!actionsMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(event.target as Node)
      ) {
        setActionsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [actionsMenuOpen]);

  /** Same status slice as the table, before manufacturer filter (e.g. active-only on "כל הרכבים"). */
  const applySortStatusFilter = useCallback(
    (carList: CarNumber[]) => {
      let filtered = carList;
      if (sortStatus === "all") {
        filtered = filtered.filter((car) => car.isActive === true);
      } else if (sortStatus === "tsumod") {
        filtered = filtered.filter((car) => car.category === "צמוד");
      } else if (sortStatus === "maagar") {
        filtered = filtered.filter((car) => car.category === "מאגר");
      } else if (sortStatus === "rented") {
        filtered = filtered.filter((car) => car.category === "שכור");
      } else if (sortStatus === "blocked") {
        filtered = filtered.filter((car) => car.blocked === true);
      } else if (sortStatus === "inactive") {
        filtered = filtered.filter((car) => car.isActive === false);
      } else if (sortStatus === "vip") {
        filtered = filtered.filter((car) => car.vip === true);
      }
      return filtered;
    },
    [sortStatus],
  );

  /** API manufacturers that actually appear in the current tab (avoids e.g. inactive-only names on "כל הרכבים"). */
  const manufacturersInCurrentView = useMemo(() => {
    const pool = applySortStatusFilter(cars);
    const present = new Set(
      pool.map((c) => (c.manufacturer || "").trim()).filter(Boolean),
    );
    return manufacturers.filter((m) => present.has(m.trim()));
  }, [cars, manufacturers, applySortStatusFilter]);

  useEffect(() => {
    const m = selectedManufacturer.trim();
    if (!m) return;
    const allowed = new Set(manufacturersInCurrentView.map((x) => x.trim()));
    if (!allowed.has(m)) {
      setSelectedManufacturer("");
    }
  }, [selectedManufacturer, manufacturersInCurrentView]);

  // Filter cars by status and manufacturer
  const getFilteredCars = () => {
    let filtered = applySortStatusFilter(cars);

    // Apply manufacturer filter (trim: API list had leading spaces e.g. " קיה" vs DB "קיה")
    if (selectedManufacturer) {
      const m = selectedManufacturer.trim();
      filtered = filtered.filter(
        (car) => (car.manufacturer || "").trim() === m,
      );
    }

    return filtered;
  };

  const sortedFilteredCars = useMemo(() => {
    const sorted = [...getFilteredCars()].sort((a, b) => {
      if (sortColumn === "expirationDate") {
        const aDate = a.expirationDate ? new Date(a.expirationDate).getTime() : 0;
        const bDate = b.expirationDate ? new Date(b.expirationDate).getTime() : 0;
        return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
      }
      if (sortColumn === "category") {
        const aCat = a.category || "";
        const bCat = b.category || "";
        return sortDirection === "asc"
          ? aCat.localeCompare(bCat, undefined, { sensitivity: "base" })
          : bCat.localeCompare(aCat, undefined, { sensitivity: "base" });
      }
      if (sortColumn === "receivedCodes") {
        const aHasCodes = a.receivedCodes && a.receivedCodes.length > 0 ? 1 : 0;
        const bHasCodes = b.receivedCodes && b.receivedCodes.length > 0 ? 1 : 0;
        return sortDirection === "asc"
          ? aHasCodes - bHasCodes
          : bHasCodes - aHasCodes;
      }
      return 0;
    });
    return sorted;
  }, [cars, sortStatus, selectedManufacturer, sortColumn, sortDirection]);

  const visibleCars = useMemo(
    () => sortedFilteredCars.slice(0, visibleRowsCount),
    [sortedFilteredCars, visibleRowsCount]
  );

  useEffect(() => {
    const nextVisibleCount = Math.min(400, sortedFilteredCars.length || 400);
    setVisibleRowsCount(nextVisibleCount);
  }, [sortedFilteredCars.length, sortStatus]);

  // Helper to load all cars before sorting
  const loadAllCars = async () => {
    let page = 1; // Always start from page 1
    let allCars: CarNumber[] = [];
    let more: boolean = true;

    while (more) {
      try {
        let url = isRental
          ? `${config.backendUrl}/car-numbers/rental-list?page=${page}&limit=2000`
          : `${config.backendUrl}/car-numbers/list?page=${page}&limit=2000`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        });
        const data = await res.json();
        const carsList = data.carNumbers || [];
        allCars = [...allCars, ...carsList];
        more =
          data.hasMore !== undefined ? data.hasMore : carsList.length === 2000;
        page++;
      } catch (e) {
        more = false;
      }
    }
    setCars([...allCars]);
    setHasMore(false);
    setCurrentPage(page - 1);
  };

  const handleSortTabClick = async (
    next: "all" | "tsumod" | "maagar" | "rented" | "blocked" | "inactive" | "vip"
  ) => {
    if (hasMore) {
      setLoading(true);
      await loadAllCars();
      setLoading(false);
    }
    setSortStatus(next);
  };

  const filtersActive =
    Boolean(searchQuery.trim()) ||
    Boolean(selectedManufacturer.trim()) ||
    sortStatus !== "all";

  const handleClearAllFilters = async () => {
    setSearchQuery("");
    setSearchActive(false);
    setSelectedManufacturer("");
    setSortStatus("all");
    setSortColumn("receivedCodes");
    setSortDirection("desc");
    setLoading(true);
    try {
      await loadAllCars();
    } finally {
      setLoading(false);
    }
  };

  // Trigger CarsSummary reload
  const triggerCarsSummaryReload = () => {
    window.dispatchEvent(new Event("reloadCarsSummary"));
  };

  // Handler to toggle blocked status
  const handleToggleBlocked = async (car: CarNumber) => {
    setUpdatingBlocked(car.carNumber);
    try {
      const res = await fetch(
        `${config.backendUrl}/car-numbers/update-blocked/${car.carNumber}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ blocked: !car.blocked }),
        }
      );
      if (!res.ok) throw new Error("Failed to update");
      setCars((prev) =>
        prev.map((c) =>
          c.carNumber === car.carNumber ? { ...c, blocked: !c.blocked } : c
        )
      );
    } catch (e) {
      alert("שגיאה בעדכון סטטוס חסום");
    } finally {
      setUpdatingBlocked(null);
    }
  };

  const handleReactivateCar = async (car: CarNumber) => {
    setReactivatingCarNumber(car.carNumber);
    try {
      const res = await fetch(
        `${config.backendUrl}/car-numbers/update-active/${car.carNumber}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({ isActive: true }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(
          typeof data.error === "string" ? data.error : "Failed to update"
        );
      setCars((prev) =>
        prev.map((c) =>
          c.carNumber === car.carNumber ? { ...c, isActive: true } : c
        )
      );
      triggerCarsSummaryReload();
    } catch (e) {
      alert("שגיאה בהחזרת הרכב לפעילים");
    } finally {
      setReactivatingCarNumber(null);
    }
  };

  // Handler to toggle VIP status
  const handleToggleVip = async (car: CarNumber) => {
    setUpdatingVip(car.carNumber);
    try {
      const newVipStatus = !car.vip;
      const newVipType = newVipStatus ? car.vipType || "unlimited" : null;

      const res = await fetch(
        `${config.backendUrl}/car-numbers/update-vip/${car.carNumber}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            vip: newVipStatus,
            vipType: newVipType,
          }),
        }
      );
      if (!res.ok) throw new Error("Failed to update");
      setCars((prev) =>
        prev.map((c) =>
          c.carNumber === car.carNumber
            ? { ...c, vip: newVipStatus, vipType: newVipType }
            : c
        )
      );
    } catch (e) {
      alert("שגיאה בעדכון סטטוס VIP");
    } finally {
      setUpdatingVip(null);
    }
  };

  // Handler to update VIP type only (without toggling VIP status)
  const handleUpdateVipType = async (
    car: CarNumber,
    vipType: "unlimited" | "one-time"
  ) => {
    if (!car.vip) return; // Don't update type if VIP is disabled

    setUpdatingVip(car.carNumber);
    try {
      const res = await fetch(
        `${config.backendUrl}/car-numbers/update-vip/${car.carNumber}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            vip: true,
            vipType: vipType,
          }),
        }
      );
      if (!res.ok) throw new Error("Failed to update");
      setCars((prev) =>
        prev.map((c) =>
          c.carNumber === car.carNumber
            ? { ...c, vip: true, vipType: vipType }
            : c
        )
      );
      // Clear VIP type state for this car
      setVipTypeState((prev) => {
        const updated = { ...prev };
        delete updated[car.carNumber];
        return updated;
      });
    } catch (e) {
      alert("שגיאה בעדכון סוג VIP");
    } finally {
      setUpdatingVip(null);
    }
  };

  // Download Excel file from backend
  const handleDownloadExcel = async () => {
    // Map sortStatus to backend group param

    let group: "all" | "tsumod" | "maagar" | "rented" | "allRental" = "all";
    if (isRental) group = "allRental";
    if (sortStatus === "tsumod") group = "tsumod";
    else if (sortStatus === "maagar") group = "maagar";
    else if (sortStatus === "rented") group = "rented";

    setLoading(true);
    try {
      const res = await fetch(
        `${config.backendUrl}/car-numbers/export-cars-excel/${group}`,
        {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      if (!res.ok) throw new Error("Failed to download Excel file");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "car_numbers.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setLoading(false);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("שגיאה בהורדת קובץ אקסל");
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      getCars(nextPage, false, searchQuery.trim() || undefined);
    }
  }, [currentPage, loadingMore, hasMore, searchQuery]);

  const handleScroll = useCallback(() => {
    if (tableContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        tableContainerRef.current;
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadMore();
        if (visibleRowsCount < sortedFilteredCars.length) {
          const nextVisibleCount = Math.min(
            visibleRowsCount + 300,
            sortedFilteredCars.length
          );
          setVisibleRowsCount(nextVisibleCount);
        }
      }
    }
  }, [loadMore, visibleRowsCount, sortedFilteredCars.length]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll, cars]);

  // Focus the search input after search results update
  useEffect(() => {
    if (searchActive && searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  }, [cars, searchActive]);

  const handleDeleteCar = (car: CarNumber) => {
    setSelectedCar(car);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedCar) {
      await fetch(
        `${config.backendUrl}/car-numbers/delete-car-number/${selectedCar.carNumber}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      setReloadCars((r) => r + 1);
      triggerCarsSummaryReload();
      triggerCarsSummaryReload();
      setShowDeleteModal(false);
      setSelectedCar(null);
    }
  };

  const handleDeleteModalCancel = () => {
    setShowDeleteModal(false);
    setSelectedCar(null);
  };

  // Helper to translate key s to Hebrew titles
  const translateKeyToHebrew = (key: string) => {
    const map: Record<string, string> = {
      carNumberInRafael: "מספר רכב רשום",
      fullName: "שם מלא",
      idNumber: "תעודת זהות",
      alternateCarNumber: "מספר רכב חלופי",
      phone: "טלפון",
      name: "שם",
      reason: "סיבה",
      receivedCode: "מספר תדלוק",
      receivedCodeDate: "נשלח בתאריך",
    };
    return (
      map[key] ||
      key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())
    );
  };

  // Helper to format date strings to dd/mm/yyyy HH:MM
  const formatDateTime = (value: string) => {
    // Handle ISO date strings without timezone conversion
    if (value.includes("T") || value.includes("-")) {
      const dateOnly = value.split("T")[0]; // Get just the date part (YYYY-MM-DD)
      const [year, month, day] = dateOnly.split("-");
      if (year && month && day) {
        return `${day}/${month}/${year}`;
      }
    }

    // Fallback for other formats
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()}`;
  };

  // if (loading) {
  //   return (
  //     <div
  //       style={{
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //         height: "50vh",
  //       }}
  //     >
  //       {/* <ClipLoader color="#149be6" size={40} /> */}
  //     </div>
  //   );
  // }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        {/* Code Search Input */}
        <div className={styles.actionsContainer}>
          <div
            className={styles.searchContainer}
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <UniversalSearchInput
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchInputRef={searchInputRef}
            />
            {/* Manufacturer Filter */}
            <div
              className={styles.manufacturerFilterWrap}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <SelectBox
                value={selectedManufacturer}
                onChange={async (e) => {
                  const newManufacturer = e.target.value.trim();
                  // If selecting a manufacturer filter, load all cars first
                  if (newManufacturer && hasMore) {
                    setLoading(true);
                    await loadAllCars();
                    setLoading(false);
                  }
                  setSelectedManufacturer(newManufacturer);
                }}
                options={[
                  { value: "", label: "כל היצרנים" },
                  ...manufacturersInCurrentView.map((manufacturer) => ({
                    value: manufacturer,
                    label: manufacturer,
                  })),
                ]}
                width={250}
                rtl
                size="medium"
                color="outline"
                dropdownMaxHeight={'200px'}
              />
            </div>
            <span style={{ display: filtersActive ? undefined : "none" }}>
              <DSButton
                buttonType="button"
                label="נקה"
                size="medium"
                color="outlineAccent"
                onClick={handleClearAllFilters}
                width="100px"
              />
            </span>
          </div>
          <div
            className={`${styles.dropdownMenu} ${styles.carsListActionsDropdown}`}
            ref={actionsMenuRef}
          >
            <div
              onClick={handleToggleActionsMenu}
              style={{
                width: "25px",
                height: "25px",
                cursor: "pointer",
              }}
            >
              <Menu3Dots backgroundColor="var(--light-gray-color)" />
            </div>
            <AnimatePresence>
              {actionsMenuOpen && (
                <motion.div
                  className={styles.menuContent}
                  initial={{ opacity: 1, scale: 0.95, y: -6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -6 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => {
                      handleDownloadExcel();
                      setActionsMenuOpen(false);
                    }}
                  >
                    <div
                      style={{ position: "relative", top: 0, height: "15px" }}
                    >
                      <DownloadIcon outline={true} />
                    </div>
                    הורד אקסל
                  </button>

                  <label htmlFor="xslFileMain" className={styles.menuItem}>
                    <input
                      id="xslFileMain"
                      hidden
                      type="file"
                      accept=".xls,.xlsx"
                      disabled={loading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0] || null;
                        if (file) {
                          try {
                            setLoading(true);
                            setActionsMenuOpen(false);
                            // First, upload and parse the file
                            const formData = new FormData();
                            formData.append("file", file);
                            formData.append(
                              "userToken",
                              localStorage.getItem("token") || ""
                            );

                            const uploadResponse = await fetch(
                              `${config.backendUrl}/car-numbers/upload-xsl-file`,
                              {
                                method: "POST",
                                headers: {
                                  "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
                                },
                                body: formData,
                              }
                            );

                            if (!uploadResponse.ok) {
                              const errorData = await uploadResponse.json();
                              throw new Error(
                                errorData.error || "שגיאה בהעלאת הקובץ"
                              );
                            }

                            const uploadResult = await uploadResponse.json();
                            // console.log(uploadResult.data);

                            // Always show upload results
                            const { total, successful, failed, updated } =
                              uploadResult.data;
                            const successMessage = `סה"כ: ${total}, הצליח: ${successful}, נכשל: ${failed}, עודכן: ${updated}`;

                            if (failed > 0) {
                              console.log(uploadResult.data.errors);
                              // Format errors for display with success summary
                              const formattedErrors = [
                                successMessage,
                                ...uploadResult.data.errors.map(
                                  (error: any) =>
                                    `מספר רכב: ${error.carNumber} - ${error.error}`
                                ),
                              ];
                              setUploadSummary(formattedErrors);
                              setShowSummaryModal(true);
                            } else {
                              // Show success message for fully successful uploads
                              setUploadSummary([successMessage]);
                              setShowSummaryModal(true);
                            }

                            setReloadCars((r) => r + 1);
                            triggerCarsSummaryReload();
                          } catch (err) {
                            console.error("Error uploading XSL file:", err);
                            const errorMessage =
                              err instanceof Error
                                ? err.message
                                : "שגיאה לא ידועה";
                            console.log(`שגיאה בהעלאת הקובץ: ${errorMessage}`);
                          } finally {
                            setLoading(false);
                            // Reset the input value so the same file can be selected again
                            e.target.value = "";
                          }
                        }
                      }}
                    />
                    <div
                      style={{ position: "relative", top: 0, height: "15px" }}
                    >
                      <ExcelIcon />
                    </div>
                    הוסף מקובץ
                  </label>

                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = `${config.backendUrl}/download/sample-car-numbers`;
                      link.download = "sample_car_numbers.xlsx";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      setActionsMenuOpen(false);
                    }}
                  >
                    קובץ לדוגמה
                  </button>

                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => {
                      // Reset form fields
                      setAddCarNumber("");
                      setAddCategory("צמוד");
                      setAddStartDate("");
                      setAddExpirationDate("");
                      setAddManufacturer("");
                      setAddModel("");
                      setAddUnit("");
                      setAddVip(false);
                      setAddVipType("unlimited");
                      setAddError("");
                      setShowAddCarModal(true);
                      setActionsMenuOpen(false);
                    }}
                  >
                    <div
                      style={{ position: "relative", top: 0, height: "15px" }}
                    >
                      <AddItemIcon />
                    </div>
                    הוסף רכב יחיד
                  </button>
                  <button
                    type="button"
                    className={`${styles.menuItem} ${
                      selectedCars.length > 0 ? styles.menuItemDelete : ""
                    }`}
                    onClick={() => {
                      setShowBulkDeleteModal(true);
                      setActionsMenuOpen(false);
                    }}
                    disabled={selectedCars.length === 0}
                    style={{ opacity: selectedCars.length === 0 ? 0.5 : 1 }}
                  >
                    <div
                      style={{ position: "relative", top: 0, height: "15px" }}
                    >
                      <TrashIcon />
                    </div>
                    מחק נבחרים
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Sort Buttons */}
        <div
          className={`${styles.sortButtonsContainer} ${styles.sortButtonsScrollOnMobile}`}
        >
          <DSButton
            id="all"
            size="small"
            color={sortStatus === "all" ? "secondary" : "outlineSecondary"}
            label="כל הרכבים"
            onClick={() => {
              void handleSortTabClick("all");
            }}
          />
          {!isRental ? (
            <DSButton
              id="tsumod"
              size="small"
              color={sortStatus === "tsumod" ? "secondary" : "outlineSecondary"}
              label="צמודים"
              onClick={() => {
                void handleSortTabClick("tsumod");
              }}
            />
          ) : null}
          <DSButton
            id="maagar"
            size="small"
            color={sortStatus === "maagar" ? "secondary" : "outlineSecondary"}
            label="מאגר"
            onClick={() => {
              void handleSortTabClick("maagar");
            }}
          />
          <DSButton
            id="rented"
            size="small"
            color={sortStatus === "rented" ? "secondary" : "outlineSecondary"}
            label="שכורים"
            onClick={() => {
              void handleSortTabClick("rented");
            }}
          />
          <DSButton
            id="blocked"
            size="small"
            color={sortStatus === "blocked" ? "secondary" : "outlineSecondary"}
            label="חסומים"
            onClick={() => {
              void handleSortTabClick("blocked");
            }}
          />
          <DSButton
            id="inactive"
            size="small"
            color={sortStatus === "inactive" ? "secondary" : "outlineSecondary"}
            label="לא פעילים"
            onClick={() => {
              void handleSortTabClick("inactive");
            }}
          />
          <DSButton
            id="vip"
            size="small"
            color={sortStatus === "vip" ? "secondary" : "outlineSecondary"}
            label="VIP"
            onClick={() => {
              void handleSortTabClick("vip");
            }}
          />
        </div>
        {/* Show summary based on selected category */}
        {sortStatus === "all" && (
          <AllCarsSummary isRental={isRental || false} />
        )}
        {sortStatus === "tsumod" && <TsumodCarsSummary />}
        {sortStatus === "maagar" && <MaagarCarsSummary />}
        {sortStatus === "rented" && <RentedCarsSummary />}
        {sortStatus === "inactive" && (
          <InactiveCarsSummary count={sortedFilteredCars.length} />
        )}
        {!Array.isArray(cars) || cars.length === 0 ? (
          <p style={{ textAlign: "center", marginTop: 60, fontSize: "20px" }}>
            {!initialLoadDoneRef.current ? "טוען מספרי רכבים..." : (!loading ? "לא נמצאו מספרי רכבים" : "טוען מספרי רכבים...")}
          </p>
        ) : (
          <div ref={tableContainerRef} className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th className={styles.tableHeaderCell}>
                    <input
                      type="checkbox"
                      checked={
                        selectedCars.length === sortedFilteredCars.length &&
                        sortedFilteredCars.length > 0
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCars(sortedFilteredCars.map((c) => c.carNumber));
                        } else {
                          setSelectedCars([]);
                        }
                        setLastSelectedIndex(null);
                      }}
                    />
                  </th>
                  <th className={styles.tableHeaderCell}>מספר רכב רשום</th>
                  <th className={styles.tableHeaderCell}>מזהה מערכת</th>
                  <th className={styles.tableHeaderCell}>יצרן</th>
                  <th className={styles.tableHeaderCell}>שם פרטי</th>
                  <th className={styles.tableHeaderCell}>שם משפחה</th>
                  <th className={styles.tableHeaderCell}>תעודת זהות</th>
                  <th className={styles.tableHeaderCell}>טלפון נייד</th>
                  <th
                    className={styles.tableHeaderCell}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      if (sortColumn === "category") {
                        setSortDirection(
                          sortDirection === "asc" ? "desc" : "asc"
                        );
                      } else {
                        setSortColumn("category");
                        setSortDirection("asc");
                      }
                    }}
                  >
                    קטגוריה
                    <span
                      style={{
                        marginRight: 4,
                        verticalAlign: "middle",
                        color: sortColumn === "category" ? undefined : "#bbb",
                        display: "inline-block",
                        transition: "color 0.2s",
                      }}
                    >
                      <SortIcon />
                    </span>
                  </th>
                  <th
                    className={styles.tableHeaderCell}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      if (sortColumn === "expirationDate") {
                        setSortDirection(
                          sortDirection === "asc" ? "desc" : "asc"
                        );
                      } else {
                        setSortColumn("expirationDate");
                        setSortDirection("asc");
                      }
                    }}
                  >
                    תוקף
                    <span
                      style={{
                        marginRight: 4,
                        verticalAlign: "middle",
                        color:
                          sortColumn === "expirationDate" ? undefined : "#bbb",
                        display: "inline-block",
                        transition: "color 0.2s",
                      }}
                    >
                      <SortIcon />
                    </span>
                  </th>
                  <th className={styles.tableHeaderCell}>יחידה</th>
                  <th className={styles.tableHeaderCell}>נוצר על ידי</th>
                  <th className={styles.tableHeaderCell}>תאריך יצירה</th>
                  <th className={styles.tableHeaderCell}>
                    חסום מקבלת מספרי תדלוק
                  </th>
                  <th className={styles.tableHeaderCell}>VIP</th>
                  <th
                    className={styles.tableHeaderCell}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      if (sortColumn === "receivedCodes") {
                        setSortDirection(
                          sortDirection === "asc" ? "desc" : "asc"
                        );
                      } else {
                        setSortColumn("receivedCodes");
                        setSortDirection("asc");
                      }
                    }}
                  >
                    קודי תדלוק
                    <span
                      style={{
                        marginRight: 4,
                        verticalAlign: "middle",
                        color:
                          sortColumn === "receivedCodes" ? undefined : "#bbb",
                        display: "inline-block",
                        transition: "color 0.2s",
                      }}
                    >
                      <SortIcon />
                    </span>
                  </th>
                  <th className={styles.tableHeaderCell}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleCars.map((car, index, arr) => (
                  <tr key={car._id || index} className={styles.tableRow}>
                    <td className={styles.tableCell}>
                      <input
                        type="checkbox"
                        checked={selectedCars.includes(car.carNumber)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          let newSelected = [...selectedCars];
                          const shiftKey = (e.nativeEvent as MouseEvent)
                            .shiftKey;
                          if (shiftKey && lastSelectedIndex !== null) {
                            const sortedCars = [...arr];
                            const last = lastSelectedIndex;
                            const current = index;
                            const [start, end] = [
                              Math.min(last, current),
                              Math.max(last, current),
                            ];
                            const rangeCarNumbers = sortedCars
                              .slice(start, end + 1)
                              .map((c) => c.carNumber);
                            if (checked) {
                              newSelected = Array.from(
                                new Set([...newSelected, ...rangeCarNumbers])
                              );
                            } else {
                              newSelected = newSelected.filter(
                                (cn) => !rangeCarNumbers.includes(cn)
                              );
                            }
                          } else {
                            if (checked) {
                              newSelected.push(car.carNumber);
                            } else {
                              newSelected = newSelected.filter(
                                (cn) => cn !== car.carNumber
                              );
                            }
                          }
                          setSelectedCars(newSelected);
                          setLastSelectedIndex(index);
                        }}
                      />
                    </td>
                    <td className={styles.tableCell}>
                      {sortStatus === "inactive" ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            alignItems: "stretch",
                          }}
                        >
                          <span>{car.carNumber}</span>
                          <Button
                            color="green"
                            size="small"
                            disabled={reactivatingCarNumber === car.carNumber}
                            onClick={() => handleReactivateCar(car)}
                          >
                            החזר לפעילים
                          </Button>
                        </div>
                      ) : (
                        car.carNumber
                      )}
                    </td>
                    <td className={styles.tableCell}>
                      {car.systemIdentifier || "-"}
                    </td>
                    <td className={styles.tableCell}>
                      {car.manufacturer || "-"}
                    </td>
                    <td className={styles.tableCell}>{car.firstName || "-"}</td>
                    <td className={styles.tableCell}>{car.lastName || "-"}</td>
                    <td className={styles.tableCell}>
                      {car.personalId || "-"}
                    </td>
                    <td className={styles.tableCell}>
                      {car.mobilePhone || "-"}
                    </td>
                    <td className={styles.tableCell}>{car.category || "-"}</td>
                    <td className={styles.tableCell}>
                      {(car.category === "שכור" &&
                        (car.startDate || car.expirationDate)) ||
                      (car.category !== "שכור" && car.expirationDate) ? (
                        <div style={{ fontSize: "12px", lineHeight: "1.3" }}>
                          {car.category === "שכור" && (
                            <div>
                              <strong>מ-</strong>{" "}
                              {car.startDate
                                ? formatDateTime(car.startDate)
                                : "לא מוגדר"}
                            </div>
                          )}
                          {car.expirationDate && (
                            <div>
                              <strong>עד-</strong>{" "}
                              {formatDateTime(car.expirationDate)}
                            </div>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className={styles.tableCell}>{car.unit || "-"}</td>
                    <td className={styles.tableCell}>
                      {car.createdByName || "-"}
                    </td>
                    <td className={styles.tableCell}>
                      {car.createdAt &&
                      car.createdAt !== null &&
                      car.createdAt !== undefined &&
                      car.createdAt !== "" &&
                      car.createdByName &&
                      car.createdByName !== null &&
                      car.createdByName !== undefined &&
                      car.createdByName !== ""
                        ? formatDateTime(car.createdAt)
                        : "-"}
                    </td>
                    <td className={styles.tableCell}>
                      <Switch
                        checked={!!car.blocked}
                        onChange={() => handleToggleBlocked(car)}
                        color="primary"
                        disabled={updatingBlocked === car.carNumber}
                        inputProps={{ "aria-label": "blocked" }}
                      />
                    </td>
                    <td className={styles.tableCell}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                          alignItems: "center",
                        }}
                      >
                        <Switch
                          checked={!!car.vip}
                          onChange={() => handleToggleVip(car)}
                          color="primary"
                          disabled={updatingVip === car.carNumber}
                          inputProps={{ "aria-label": "vip" }}
                        />
                        {car.vip && (
                          <SelectBox
                            value={
                              vipTypeState[car.carNumber] ||
                              car.vipType ||
                              "unlimited"
                            }
                            onChange={(e) => {
                              const selectedType = e.target.value as
                                | "unlimited"
                                | "one-time";
                              setVipTypeState((prev) => ({
                                ...prev,
                                [car.carNumber]: selectedType,
                              }));
                              handleUpdateVipType(car, selectedType);
                            }}
                            options={[
                              { value: "unlimited", label: "בקשות ללא הגבלה" },
                              { value: "one-time", label: "אפשר בקשה נוספת" },
                            ]}
                            width={150}
                            rtl
                            size="medium"
                            color="outline"
                            style={{ marginTop: 4 }}
                          />
                        )}
                      </div>
                    </td>
                    <td className={styles.tableCell}>
                      {car.receivedCodes && car.receivedCodes.length > 0
                        ? car.receivedCodes.map((codeInfo, index) => (
                            <div
                              key={index}
                              style={{ marginBottom: "4px", fontSize: "12px" }}
                            >
                              <span style={{ fontWeight: "bold" }}>
                                {codeInfo.code}
                              </span>
                              <br />
                              <span style={{ color: "#666" }}>
                                {formatDateTime(codeInfo.dateReceived)}
                              </span>
                            </div>
                          ))
                        : "-"}
                    </td>
                    <td className={styles.actionCell}>
                      <div className={styles.deleteButtonContainer}>
                        <Button
                          color="blue"
                          size="small"
                          onClick={() => {
                            setEditCar(car);
                            setEditCategory(car.category || "צמוד");
                            setEditStartDate(
                              car.startDate ? car.startDate.slice(0, 10) : ""
                            );
                            setEditExpirationDate(
                              car.expirationDate
                                ? car.expirationDate.slice(0, 10)
                                : ""
                            );
                            setEditSystemIdentifier(car.systemIdentifier || "");
                            setEditDealNumber(car.dealNumber || "");
                            setEditFirstName(car.firstName || "");
                            setEditLastName(car.lastName || "");
                            setEditMobilePhone(car.mobilePhone || "");
                            setEditPersonalId(car.personalId || "");
                            setEditManufacturer(car.manufacturer || "");
                            setEditModel(car.model || "");
                            setEditUnit(car.unit || "");
                            setEditVip(car.vip || false);
                            setEditVipType(car.vipType || "unlimited");
                            setEditError("");
                            setShowEditModal(true);
                          }}
                          style={{ marginLeft: 8 }}
                        >
                          ערוך
                        </Button>
                        <Button
                          color="red"
                          size="small"
                          onClick={() => handleDeleteCar(car)}
                        >
                          מחק
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Loading more indicator */}
            {loadingMore && (
              <div className={styles.loadingMoreIndicator}>
                <p className={styles.loadingMoreText}>טוען מספרים נוספים...</p>
              </div>
            )}
            {/* End of data indicator */}
            {!hasMore && cars.length > 0 && (
              <div className={styles.endOfDataIndicator}>
                אין עוד מספרים לטעינה
              </div>
            )}
            {visibleRowsCount < sortedFilteredCars.length && (
              <div className={styles.endOfDataIndicator}>
                מוצגים {visibleRowsCount} מתוך {sortedFilteredCars.length} רכבים
              </div>
            )}
          </div>
        )}
        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedCar && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <h3 className={styles.modalTitle}>
                האם אתה בטוח שברצונך למחוק את המספר {selectedCar.carNumber}?
              </h3>
              <div className={styles.modalActions}>
                <Button
                  color="gray"
                  size="medium"
                  onClick={handleDeleteModalCancel}
                >
                  ביטול
                </Button>
                <Button
                  color="red"
                  size="medium"
                  onClick={handleConfirmDelete}
                >
                  מחק
                </Button>
              </div>
            </div>
          </div>
        )}

        {showBulkDeleteModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <h3 className={styles.modalTitle}>
                האם אתה בטוח שברצונך למחוק את המספרים שנבחרו (
                {selectedCars.length})?
              </h3>
              <div className={styles.modalActions}>
                <Button
                  color="gray"
                  size="medium"
                  onClick={() => setShowBulkDeleteModal(false)}
                >
                  ביטול
                </Button>
                <Button
                  color="red"
                  size="medium"
                  onClick={async () => {
                    await fetch(
                      `${config.backendUrl}/car-numbers/delete-many-car-numbers`,
                      {
                        method: "DELETE",
                        headers: {
                          "Content-Type": "application/json",
                          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
                        },
                        body: JSON.stringify({ carNumbers: selectedCars }),
                      }
                    );
                    setReloadCars((r) => r + 1);
                    triggerCarsSummaryReload();
                    setShowBulkDeleteModal(false);
                    setSelectedCars([]);
                  }}
                >
                  מחק
                </Button>
              </div>
            </div>
          </div>
        )}
        {showEditModal && editCar && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <h3 className={styles.modalTitle}>עריכת מספר רכב</h3>
              {editError && (
                <div style={{ color: "red", marginBottom: 8 }}>{editError}</div>
              )}
              <div
                style={{ marginBottom: 12, maxHeight: 400, overflowY: "auto" }}
              >
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>מספר רכב רשום:</label>
                  <input
                    type="text"
                    value={editCar.carNumber}
                    disabled
                    style={{ width: "100%", background: "#f0f0f0" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>מזהה מערכת:</label>
                  <input
                    type="text"
                    value={editSystemIdentifier}
                    onChange={(e) => setEditSystemIdentifier(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>מספר עסקה:</label>
                  <input
                    type="text"
                    value={editDealNumber}
                    onChange={(e) => setEditDealNumber(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>שם פרטי:</label>
                  <input
                    type="text"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>שם משפחה:</label>
                  <input
                    type="text"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>טלפון נייד:</label>
                  <input
                    type="text"
                    value={editMobilePhone}
                    onChange={(e) => setEditMobilePhone(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>תעודת זהות:</label>
                  <input
                    type="text"
                    value={editPersonalId}
                    onChange={(e) => setEditPersonalId(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>יצרן:</label>
                  <input
                    type="text"
                    value={editManufacturer}
                    onChange={(e) => setEditManufacturer(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>דגם:</label>
                  <input
                    type="text"
                    value={editModel}
                    onChange={(e) => setEditModel(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>יחידה:</label>
                  <input
                    type="text"
                    value={editUnit}
                    onChange={(e) => setEditUnit(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>קטגוריה:</label>
                  <SelectBox
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    options={[
                      { value: "צמוד", label: "צמוד" },
                      { value: "מאגר", label: "מאגר" },
                      { value: "שכור", label: "שכור" },
                    ]}
                    width="100%"
                    rtl
                    size="medium"
                    color="outline"
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>תאריך התחלה:</label>
                  <input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    style={{ width: "100%" }}
                    disabled={editCategory !== "שכור"}
                  />
                  {editCategory === "שכור" && !editStartDate && (
                    <small style={{ color: "#666", fontSize: "12px" }}>
                      אם לא נבחר תאריך, יוגדר היום כתאריך התחלה
                    </small>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>תאריך סיום:</label>
                  <input
                    type="date"
                    value={editExpirationDate}
                    onChange={(e) => setEditExpirationDate(e.target.value)}
                    style={{ width: "100%" }}
                    disabled={editCategory !== "שכור"}
                  />
                </div>
                <div
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <label style={{ fontWeight: 500 }}>VIP:</label>
                  <Switch
                    checked={editVip}
                    onChange={(e) => setEditVip(e.target.checked)}
                    color="primary"
                    inputProps={{ "aria-label": "vip" }}
                  />
                </div>
                {editVip && (
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontWeight: 500 }}>סוג VIP:</label>
                    <SelectBox
                      value={editVipType}
                      onChange={(e) =>
                        setEditVipType(
                          e.target.value as "unlimited" | "one-time"
                        )
                      }
                      options={[
                        { value: "unlimited", label: "בקשות ללא הגבלה" },
                        { value: "one-time", label: "אפשר בקשה נוספת" },
                      ]}
                      width="100%"
                      rtl
                      size="medium"
                      color="outline"
                    />
                  </div>
                )}
              </div>
              <div className={styles.modalActions}>
                <Button
                  color="gray"
                  size="medium"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditCar(null);
                    setEditError("");
                    setEditCategory("");
                    setEditStartDate("");
                    setEditExpirationDate("");
                    setEditSystemIdentifier("");
                    setEditDealNumber("");
                    setEditFirstName("");
                    setEditLastName("");
                    setEditMobilePhone("");
                    setEditPersonalId("");
                    setEditManufacturer("");
                    setEditModel("");
                    setEditUnit("");
                    setEditVip(false);
                    setEditVipType("unlimited");
                  }}
                >
                  ביטול
                </Button>
                <Button
                  color="green"
                  size="medium"
                  onClick={async () => {
                    setEditError("");
                    try {
                      const res = await fetch(
                        `${config.backendUrl}/car-numbers/update-car-number/${editCar.carNumber}`,
                        {
                          method: "PUT",
                          headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
                          },
                          body: JSON.stringify({
                            systemIdentifier: editSystemIdentifier || null,
                            dealNumber: editDealNumber || null,
                            firstName: editFirstName || null,
                            lastName: editLastName || null,
                            mobilePhone: editMobilePhone || null,
                            personalId: editPersonalId || null,
                            manufacturer: editManufacturer || null,
                            model: editModel || null,
                            unit: editUnit || null,
                            category: editCategory,
                            startDate:
                              editCategory === "שכור"
                                ? editStartDate.trim() ||
                                  new Date().toISOString().split("T")[0]
                                : null,
                            expirationDate:
                              editCategory === "שכור" && editExpirationDate
                                ? editExpirationDate
                                : null,
                            vip: editVip,
                            vipType: editVip ? editVipType : null,
                          }),
                        }
                      );
                      const data = await res.json();
                      if (!res.ok)
                        throw new Error(data.error || "שגיאה בעדכון מספר רכב");
                      setReloadCars((r) => r + 1);
                      triggerCarsSummaryReload();
                      setShowEditModal(false);
                      setEditCar(null);
                      setEditError("");
                      setEditCategory("");
                      setEditStartDate("");
                      setEditExpirationDate("");
                      setEditSystemIdentifier("");
                      setEditDealNumber("");
                      setEditFirstName("");
                      setEditLastName("");
                      setEditMobilePhone("");
                      setEditPersonalId("");
                      setEditManufacturer("");
                      setEditModel("");
                      setEditUnit("");
                      setEditVip(false);
                      setEditVipType("unlimited");
                    } catch (err: any) {
                      setEditError(err.message);
                    }
                  }}
                  disabled={
                    !editCategory ||
                    (editCategory === "שכור" && !editExpirationDate)
                  }
                >
                  עדכן
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Car Modal */}
        {showAddCarModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
              <h3 className={styles.modalTitle}>הוסף רכב חדש</h3>
              {addError && (
                <div style={{ color: "red", marginBottom: 8 }}>{addError}</div>
              )}
              <div
                style={{ marginBottom: 12, maxHeight: 400, overflowY: "auto" }}
              >
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>מספר רכב רשום:</label>
                  <input
                    type="text"
                    value={addCarNumber}
                    onChange={(e) => setAddCarNumber(e.target.value)}
                    style={{ width: "100%" }}
                    placeholder="הזן מספר רכב"
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>קטגוריה:</label>
                  <SelectBox
                    value={addCategory}
                    onChange={(e) => setAddCategory(e.target.value)}
                    options={[
                      { value: "צמוד", label: "צמוד" },
                      { value: "מאגר", label: "מאגר" },
                      { value: "שכור", label: "שכור" },
                    ]}
                    width="100%"
                    rtl
                    size="medium"
                    color="outline"
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>תאריך התחלה:</label>
                  <input
                    type="date"
                    value={addStartDate}
                    onChange={(e) => setAddStartDate(e.target.value)}
                    style={{ width: "100%" }}
                    disabled={addCategory !== "שכור"}
                    placeholder="ברירת מחדל: היום"
                  />
                  {addCategory === "שכור" && !addStartDate && (
                    <small style={{ color: "#666", fontSize: "12px" }}>
                      אם לא נבחר תאריך, יוגדר היום כתאריך התחלה
                    </small>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>תאריך סיום:</label>
                  <input
                    type="date"
                    value={addExpirationDate}
                    onChange={(e) => setAddExpirationDate(e.target.value)}
                    style={{ width: "100%" }}
                    disabled={addCategory !== "שכור"}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>יצרן:</label>
                  <input
                    type="text"
                    value={addManufacturer}
                    onChange={(e) => setAddManufacturer(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>דגם:</label>
                  <input
                    type="text"
                    value={addModel}
                    onChange={(e) => setAddModel(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontWeight: 500 }}>יחידה:</label>
                  <input
                    type="text"
                    value={addUnit}
                    onChange={(e) => setAddUnit(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>
                <div
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <label style={{ fontWeight: 500 }}>VIP:</label>
                  <Switch
                    checked={addVip}
                    onChange={(e) => setAddVip(e.target.checked)}
                    color="primary"
                    inputProps={{ "aria-label": "vip" }}
                  />
                </div>
                {addVip && (
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontWeight: 500 }}>סוג VIP:</label>
                    <SelectBox
                      value={addVipType}
                      onChange={(e) =>
                        setAddVipType(
                          e.target.value as "unlimited" | "one-time"
                        )
                      }
                      options={[
                        { value: "unlimited", label: "בקשות ללא הגבלה" },
                        { value: "one-time", label: "אפשר בקשה נוספת" },
                      ]}
                      width="100%"
                      rtl
                      size="medium"
                      color="outline"
                    />
                  </div>
                )}
              </div>
              <div className={styles.modalActions}>
                <Button
                  color="gray"
                  size="medium"
                  onClick={() => {
                    setShowAddCarModal(false);
                    setAddCarNumber("");
                    setAddCategory("צמוד");
                    setAddStartDate("");
                    setAddExpirationDate("");
                    setAddManufacturer("");
                    setAddModel("");
                    setAddUnit("");
                    setAddVip(false);
                    setAddVipType("unlimited");
                    setAddError("");
                  }}
                >
                  ביטול
                </Button>
                <Button
                  color="green"
                  size="medium"
                  onClick={async () => {
                    setAddError("");
                    try {
                      if (!addCarNumber.trim()) {
                        throw new Error("מספר רכב הוא שדה חובה");
                      }

                      const res = await fetch(
                        `${config.backendUrl}/car-numbers/add-car-number-full`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
                          },
                          body: JSON.stringify({
                            carNumber: addCarNumber.trim(),
                            manufacturer: addManufacturer || null,
                            model: addModel || null,
                            unit: addUnit || null,
                            category: addCategory,
                            startDate:
                              addCategory === "שכור"
                                ? addStartDate.trim() ||
                                  new Date().toISOString().split("T")[0]
                                : null,
                            expirationDate:
                              addCategory === "שכור" && addExpirationDate
                                ? addExpirationDate
                                : null,
                            vip: addVip,
                            vipType: addVip ? addVipType : null,
                            userToken: localStorage.getItem("token"),
                          }),
                        }
                      );
                      const data = await res.json();
                      if (!res.ok)
                        throw new Error(data.error || "שגיאה בהוספת רכב חדש");

                      setReloadCars((r) => r + 1);
                      triggerCarsSummaryReload();
                      setShowAddCarModal(false);
                      setAddCarNumber("");
                      setAddCategory("צמוד");
                      setAddStartDate("");
                      setAddExpirationDate("");
                      setAddManufacturer("");
                      setAddModel("");
                      setAddUnit("");
                      setAddVip(false);
                      setAddVipType("unlimited");
                      setAddError("");
                    } catch (err: any) {
                      setAddError(err.message);
                    }
                  }}
                  disabled={
                    !addCarNumber.trim() ||
                    (addCategory === "שכור" && !addExpirationDate)
                  }
                >
                  הוסף
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showDetailsModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContent} style={{ maxWidth: 500 }}>
              <h3 className={styles.modalTitle}>פרטי מספר</h3>
              <div
                style={{
                  marginBottom: 16,
                  maxHeight: 300,
                  overflowY: "auto",
                  background: "#f8f9fa",
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                {detailsData && Object.keys(detailsData).length > 0 ? (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 14,
                      direction: "rtl",
                    }}
                  >
                    <tbody>
                      {Object.entries(detailsData).map(([key, value]) => (
                        <tr key={key}>
                          <td
                            style={{
                              fontWeight: "bold",
                              padding: "6px 8px",
                              borderBottom: "1px solid #eee",
                              width: "35%",
                            }}
                          >
                            {translateKeyToHebrew(key)}
                          </td>
                          <td
                            style={{
                              padding: "6px 8px",
                              borderBottom: "1px solid #eee",
                              direction: "ltr",
                              textAlign: "left",
                            }}
                          >
                            {typeof value === "string" &&
                            (key.toLowerCase().includes("date") ||
                              /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value))
                              ? formatDateTime(value)
                              : typeof value === "object" && value !== null
                              ? JSON.stringify(value)
                              : String(value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <span>אין פרטים</span>
                )}
              </div>
              <div className={styles.modalActions}>
                <Button
                  color="gray"
                  size="medium"
                  onClick={() => setShowDetailsModal(false)}
                >
                  סגור
                </Button>
              </div>
            </div>
          </div>
        )}

        <SummaryModal
          isOpen={showSummaryModal}
          onClose={() => setShowSummaryModal(false)}
          title="סיכום הוספת מספרים מקובץ"
          errors={uploadSummary}
        />
      </div>
    </div>
  );
};

export default CodesList;
